import { sendReport, extractReportIdFromResponse } from "../functions/sendReport";
import { updateGameSettings } from "../functions/updateGameSettings";

const GAME_START_KEY = "scribbleGameStartedAt";

function getScribbleElapsedSec() {
  const raw = sessionStorage.getItem(GAME_START_KEY);
  const start = raw ? Number(raw) : Date.now();
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

function formatTimeHHMMSS(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function readUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function persistReportId(reportId) {
  if (!reportId) return;
  try {
    const user = readUserData();
    sessionStorage.setItem(
      "userData",
      JSON.stringify({ ...user, reportId: String(reportId).trim() })
    );
  } catch {
    /* ignore */
  }
}

/** Ignore stage row id / login userId mistaken for Extra Mile report id. */
function isStoredReportId(reportId, userId) {
  const rid = String(reportId ?? "").trim();
  const uid = String(userId ?? "").trim();
  if (!rid) return false;
  if (uid && rid === uid) return false;
  return true;
}

/**
 * Load reportId from session or stages.reportid (never stages.id).
 */
async function loadStoredReportId(user, apiToken) {
  const userId = String(user.userId ?? user.userid ?? user.id ?? "").trim();

  const fromSession =
    user.reportId != null && isStoredReportId(user.reportId, userId)
      ? String(user.reportId).trim()
      : null;
  if (fromSession) return fromSession;

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const sessionId = user.sessionId;
  const organizationId = user.organizationId;
  if (!backendBase || !apiToken || !sessionId || !organizationId || !userId) {
    return null;
  }

  try {
    const res = await fetch(`${backendBase}/fetchReport`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      body: JSON.stringify({ userId, sessionId, organizationId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data?.success || !data?.report) return null;

    const fromDb =
      data.report.reportid != null && String(data.report.reportid).trim() !== ""
        ? String(data.report.reportid).trim()
        : data.report.reportId != null && String(data.report.reportId).trim() !== ""
          ? String(data.report.reportId).trim()
          : null;

    if (!fromDb || !isStoredReportId(fromDb, userId)) return null;

    persistReportId(fromDb);
    return fromDb;
  } catch {
    return null;
  }
}

async function persistReportIdOnStage(reportIdToSave, user, gs) {
  const apiToken =
    user.sessionId && user.organizationId
      ? `${user.sessionId}&${user.organizationId}`
      : "";
  const stageId = gs.stageId ?? null;
  const userId = String(user.userId ?? user.userid ?? user.id ?? "").trim();
  if (!reportIdToSave || !apiToken || !stageId || !userId) return;

  try {
    await updateGameSettings({
      token: apiToken,
      userId,
      stageId: String(stageId),
      themeName: gs.themeName ?? "guess_me",
      reportid: reportIdToSave,
    });
  } catch (e) {
    console.warn("[taboo] reportid stage save:", e?.message || e);
  }
}

function buildSendReportPayload({
  user,
  sessionId,
  organizationId,
  userId,
  role,
  token,
  isGuest,
  points,
  time,
  lobby,
  reportId,
}) {
  return {
    sessionId,
    organizationId,
    role: isGuest ? "GUEST_USER" : role,
    token: isGuest ? "" : token,
    name: user.name ?? user.fullName ?? "Player",
    email: user.email ?? null,
    userId,
    gameId: user.gameId ?? "",
    points,
    time,
    reportId: reportId ?? undefined,
    ans: lobby ? `GUESS_ME:${lobby}` : "",
    user_type: user.user_type ?? user.role ?? null,
    guestScoreMinimal: isGuest,
  };
}

/**
 * Extra Mile reports (via sendReport):
 * - First time: POST game-server/report/add → id in response → save session + stages.reportid
 * - After that: PUT game-server/report/{reportId} with points + time
 */
export async function syncScribbleScoreToExtraMile(totalScore, { timeOverride } = {}) {
  const user = readUserData();
  const role = String(user.role ?? user.user_type ?? user.source ?? "ORG_USER").trim();
  if (role.toLowerCase() === "demobypass") return null;

  const sessionId = user.sessionId;
  const organizationId = user.organizationId;
  const userId = String(user.userId ?? user.userid ?? user.id ?? "").trim();
  if (!sessionId || !organizationId || !userId) return null;

  const points = Math.max(0, Math.round(Number(totalScore) || 0));
  const time =
    timeOverride != null && String(timeOverride).trim() !== ""
      ? String(timeOverride).trim()
      : formatTimeHHMMSS(getScribbleElapsedSec());

  const isGuest = role.toUpperCase() === "GUEST_USER";
  const token = String(user.token ?? "").trim();
  const apiToken = `${sessionId}&${organizationId}`;

  if (!isGuest && !token) return null;

  let gs = {};
  try {
    gs = JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    gs = {};
  }

  const lobby = String(gs.lobby_name ?? user.lobby_name ?? user.lobby ?? "").trim();
  let reportId = await loadStoredReportId(user, apiToken);

  try {
    if (!reportId) {
      const createResp = await sendReport(
        buildSendReportPayload({
          user,
          sessionId,
          organizationId,
          userId,
          role,
          token,
          isGuest,
          points,
          time,
          lobby,
          reportId: null,
        })
      );

      const newRid = extractReportIdFromResponse(createResp);
      if (!newRid || !isStoredReportId(newRid, userId)) {
        console.warn("[taboo] report/add did not return a report id");
        return null;
      }

      reportId = newRid;
      persistReportId(reportId);
      await persistReportIdOnStage(reportId, user, gs);
      return { reportId, points, time };
    }

    await sendReport(
      buildSendReportPayload({
        user,
        sessionId,
        organizationId,
        userId,
        role,
        token,
        isGuest,
        points,
        time,
        lobby,
        reportId,
      })
    );

    return { reportId, points, time };
  } catch (e) {
    console.warn("[taboo] Extra Mile sync failed:", e?.message || e);
    return null;
  }
}
